import { getErrorMessage } from "utils/error";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  DateInput,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Select
} from "shared/components";
import { Edit01, Trash02, XClose } from "untitledui-js-base";
import addressApi from "api/landlord/address";
import { statuses } from "../../../shared/app/UnitsTableView/constants";
import { useForm } from "shared/hooks/use-form";
import moment from "moment";
import { useAuthContext } from "providers/Auth";
import { unitValidations } from "views/properties-id-units-add/validations";
import Drawer from "react-modern-drawer";
import { UseMutateAsyncFunction, useQueryClient } from "@tanstack/react-query";
import { Unit } from "types/landlord/address";
import { toast } from "react-toastify";
import { cx } from "shared/utils";
import { formatToCurrency } from "utils/currency";
import { EditModalContext } from "views/properties-id-units-id/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { Switch } from "shared/components/forms/Switch";
import { Money } from "types/landlord/reports";
import { useSettingsController } from "views/settings-developers/controller";
import { AllowOrDisallowApplications } from "shared/app/AllowOrDisallowApplications";
import { useMedia } from "shared/providers";

type UnitSettingsProps = {
  showSettings: boolean;
  setShowSettings: (value: boolean) => void;
  unit: Unit;
  setShowDeleteUnitModal: (value: boolean) => void;
  setShowEditModal: Dispatch<
    SetStateAction<{ isOpen: boolean; context?: EditModalContext | undefined }>
  >;
  setShowReviewModal: (value: boolean) => void;
  handleEditUnit: UseMutateAsyncFunction<
    void,
    Error,
    {
      unitId: number;
      settings: {
        occupied?: boolean;
        available_at?: string;
        pms_available_at_override?: boolean;
        pms_monthly_rent_amount_override?: boolean;
        monthly_rent_amount?: Money;
        pms_occupied_override?: boolean;
        listed?: boolean;
      };
    },
    unknown
  >;
  templateName?: string;
};

export const UnitSettings = ({
  showSettings,
  setShowSettings,
  unit,
  setShowEditModal,
  setShowDeleteUnitModal,
  handleEditUnit,
  setShowReviewModal,
  templateName
}: UnitSettingsProps) => {
  const queryClient = useQueryClient();

  const { permissions, type } = useAuthContext();
  const { isEverySyncEnabled } = useSettingsController();

  const { register, onSubmit, values, handleChange } = useForm({
    title: unit.title,
    rentAmount: (unit.monthly_rent_amount?.cents || 0) / 100,
    occupied: unit.occupied,
    available_at: unit.available_at
  });

  const [linkIsActive, setLinkIsActive] = useState<boolean>(
    Boolean(unit.magic_link?.active)
  );
  const [unitListed, setUnitListed] = useState<boolean>(Boolean(unit.listed));

  useEffect(() => {
    if (!linkIsActive && unitListed) setUnitListed(linkIsActive);
  }, [linkIsActive]);

  useEffect(() => {
    if (unitListed && !linkIsActive) setLinkIsActive(true);
  }, [unitListed]);

  const { isMobile } = useMedia();

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      zIndex={40}
      size={isMobile ? 360 : 480}
      open={showSettings}
      onClose={() => setShowSettings(false)}
    >
      <form
        className="flex h-full flex-col px-6 py-4 overflow-y-auto"
        onSubmit={onSubmit({
          onSuccess: async () => {
            const settings = {
              title: values.title,
              monthly_rent_amount: {
                cents: values.rentAmount * 100,
                currency: "USD"
              } as unknown as number,
              available_at:
                values.occupied && values.available_at
                  ? moment(values.available_at).format("YYYY-MM-DD")
                  : undefined,
              occupied: values.occupied
            };
            try {
              await unitValidations.validate(settings);
              toast
                .promise(
                  addressApi.editUnit(unit.id, settings).then(() =>
                    type.isBoomScreen && unit.magic_link?.id
                      ? addressApi.updateMagicLink(unit.magic_link.id, {
                          active: linkIsActive,
                          listed: unitListed
                        })
                      : Promise.resolve()
                  ),
                  {
                    pending: "Saving...",
                    success: "Unit settings saved",
                    error: "Error saving unit settings"
                  }
                )
                .then(() => {
                  queryClient.invalidateQueries({
                    queryKey: ["unit", String(unit.id)]
                  });
                  setShowSettings(false);
                });
            } catch (err) {
              toast.error(getErrorMessage(err));
            }
          }
        })}
      >
        <div className="flex items-center justify-between pb-4">
          <h2 className="text-xl font-bold text-content-1000">Unit Settings</h2>
          <IconButton
            variant={IconButtonVariant.BASE}
            size={IconButtonSize.SM}
            borderLess
            className="!justify-end translate-x-2"
            onClick={() => setShowSettings(false)}
            Icon={XClose}
          />
        </div>
        <div className="-mx-6 border-b border-alpha-80" />
        {type.isBoomScreen ? (
          <>
            <div className="my-4">
              <span className="text-xs text-content-700 font-medium bg-alpha-40 px-2 py-0.5 rounded-3xl">
                {unit.pms_id ? "PMS" : "Manually added"}
              </span>
            </div>

            <div className="border-b border-alpha-80" />

            <div className="mt-4">
              {isEverySyncEnabled && unit.pms_id ? (
                <AllowOrDisallowApplications
                  isActive={linkIsActive}
                  isListed={unit.listed}
                  withListedInfo
                />
              ) : (
                <div>
                  <Switch
                    label="Allow applying"
                    checked={linkIsActive}
                    className="text-base font-medium text-content-1000"
                    onChange={() => {
                      setLinkIsActive((prev) => !prev);
                    }}
                  />

                  <div className="relative">
                    {/*dashed line*/}
                    <div className="absolute border-l border-content-100 left-5 border-dashed h-5 mt-1" />
                    {/*circle*/}
                    <div className="absolute transform -translate-y-1/2 left-10 top-7 size-1.5 bg-content-100 rounded-full" />
                    {/*dashed line*/}
                    <div className="absolute top-1/2 transform -translate-y-1/2 left-5 top-7 border-dashed border-content-100 border-t w-6" />
                  </div>

                  <div className="mt-4 ml-14">
                    <Switch
                      label="Show unit on portfolio page"
                      checked={unitListed}
                      className="text-base font-medium text-content-1000"
                      onChange={() => {
                        setUnitListed((prev) => !prev);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        ) : null}
        {type.isBoomScreen && templateName ? (
          <div className="mt-6">
            <p className="mb-2 text-sm font-medium">Screening Settings</p>
            <div className="mb-2 rounded-lg border border-alpha-100 bg-alpha-40 p-4">
              <p className="text-sm font-medium">{templateName}</p>
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.SM}
                onClick={() => setShowReviewModal(true)}
              >
                Review
              </Button>
            </div>
            <div className="flex justify-end">
              <p className="text-xs">*Defined in the property settings</p>
            </div>
          </div>
        ) : null}

        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Input
              readOnly={Boolean(unit.pms_id)}
              id="unitName"
              label="Unit Name"
              placeholder="Unit Name"
              {...register("title")}
              className="mb-4"
            />
          </TooltipTrigger>
          {unit.pms_id && (
            <TooltipContent className="max-w-[320px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
              Unit name can only be edited within the PMS
            </TooltipContent>
          )}
        </Tooltip>

        <div
          className={cx(
            "flex items-center mb-4 w-full",
            unit.pms_monthly_rent_amount_override ? "" : "flex-wrap"
          )}
        >
          <Tooltip>
            <TooltipTrigger className="w-full text-left">
              <Input
                readOnly={
                  !permissions.canChangeRentAmount ||
                  Boolean(unit.pms_id && unit.pms_monthly_rent_amount_override)
                }
                id="rentAmount"
                label="Rent Amount"
                placeholder="Rent Amount"
                {...register("rentAmount")}
                type="money"
              />
            </TooltipTrigger>
            {unit.pms_id && (
              <TooltipContent className="max-w-[320px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                <p>
                  {unit.pms_monthly_rent_amount_override
                    ? `Rent amount is synchronized with PMS. You can manually edit
                  the field by disabling synchronization for this field. To do
                  this, click the ✎ (edit) button`
                    : `Rent amount is currently edited manually. You can enable
                synchronization by clicking 'Sync with PMS?'`}
                </p>
              </TooltipContent>
            )}
          </Tooltip>

          {type.isBoomScreen &&
            unit.pms_id &&
            (unit.pms_monthly_rent_amount_override ? (
              <div className="ml-4">
                <IconButton
                  Icon={Edit01}
                  onClick={() =>
                    setShowEditModal({
                      isOpen: true,
                      context: EditModalContext.RentAmount
                    })
                  }
                  size={IconButtonSize.BASE}
                  className="mt-4 w-12"
                  disabled={!permissions.canChangeRentAmount}
                />
              </div>
            ) : (
              <div className="flex w-full justify-end gap-2 items-center">
                <p className="text-xs font-medium text-content-300">
                  PMS value:
                  {formatToCurrency({
                    cents: unit.pms_monthly_rent_amount?.cents || 0,
                    currency: unit.pms_monthly_rent_amount?.currency || "USD"
                  })}
                </p>
                <p
                  className="text-xs text-primary-500 font-bold cursor-pointer"
                  onClick={() => {
                    handleEditUnit({
                      unitId: unit.id,
                      settings: {
                        pms_monthly_rent_amount_override: true,
                        monthly_rent_amount: unit.pms_monthly_rent_amount
                      }
                    }).then(() =>
                      handleChange(
                        "rentAmount",
                        () =>
                          (unit.pms_monthly_rent_amount?.cents || 0) / 100 ||
                          "0"
                      )({})
                    );
                  }}
                >
                  Sync with PMS?
                </p>
              </div>
            ))}
        </div>
        <div
          className={cx(
            "flex items-center mb-3 w-full",
            unit.pms_occupied_override ? "" : "flex-wrap"
          )}
        >
          <Tooltip>
            <TooltipTrigger className="w-full text-left">
              <Select
                isDisabled={
                  !permissions.canChangeAvailablebyDate ||
                  Boolean(unit.pms_id && unit.pms_occupied_override)
                }
                label="Current Status"
                id="occupied"
                options={statuses}
                searchable={false}
                {...register("occupied")}
              />
            </TooltipTrigger>
            {unit.pms_id && (
              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                <p>
                  {unit.pms_available_at_override
                    ? `Current status & available by date is synchronized with PMS.
                  You can manually edit these fields by disabling
                  synchronization for this field. To do this, click the ✎ (edit)
                  button`
                    : `Current status & available by date is currently edited manually.
                You can enable synchronization by clicking 'Sync with PMS'`}
                </p>
              </TooltipContent>
            )}
          </Tooltip>

          {type.isBoomScreen &&
            unit.pms_id &&
            (unit.pms_occupied_override ? (
              <div className="ml-4">
                <IconButton
                  Icon={Edit01}
                  onClick={() =>
                    setShowEditModal({
                      isOpen: true,
                      context: EditModalContext.CurrentStatus
                    })
                  }
                  size={IconButtonSize.BASE}
                  className="mt-4 w-12"
                  disabled={!permissions.canChangeAvailablebyDate}
                />
              </div>
            ) : (
              <div className="flex w-full justify-end gap-2 items-center">
                <p className="text-xs font-medium text-content-300">
                  PMS value:{" "}
                  {statuses.find((el) => el.value === unit.pms_occupied)
                    ?.label || "--"}
                </p>
                <p
                  className="text-xs text-primary-500 font-bold cursor-pointer"
                  onClick={() =>
                    handleEditUnit({
                      unitId: unit.id,
                      settings: {
                        pms_occupied_override: true,
                        pms_available_at_override: true,
                        occupied: unit.pms_occupied,
                        available_at: unit.pms_available_at
                      }
                    }).then(() => {
                      handleChange("occupied", () => unit.pms_occupied)({});
                      handleChange(
                        "available_at",
                        () => unit.pms_available_at
                      )({});
                    })
                  }
                >
                  Sync with PMS?
                </p>
              </div>
            ))}
        </div>

        <div
          className={cx(
            "flex mt-2 items-center w-full",
            unit.pms_available_at_override ? "" : "flex-wrap"
          )}
        >
          <Tooltip>
            <TooltipTrigger className="w-full text-left">
              <DateInput
                id="available_at"
                label="Available By Date"
                {...register("available_at")}
                readOnly={
                  !values.occupied ||
                  !permissions.canChangeAvailablebyDate ||
                  Boolean(unit.pms_id && unit.pms_available_at_override)
                }
              />
            </TooltipTrigger>
            {unit.pms_id && (
              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                <p>
                  {unit.pms_available_at_override
                    ? `Current status & available by date is synchronized with PMS.
                  You can manually edit these fields by disabling
                  synchronization for this field. To do this, click the ✎ (edit)
                  button`
                    : `Current status & available by date is currently
                  edited manually. You can enable synchronization by clicking
                  'Sync with PMS'`}
                </p>
              </TooltipContent>
            )}
          </Tooltip>

          {type.isBoomScreen &&
            unit.pms_id &&
            (unit.pms_occupied_override ? (
              <div className="ml-4">
                <IconButton
                  title="Edit"
                  Icon={Edit01}
                  onClick={() =>
                    setShowEditModal({
                      isOpen: true,
                      context: EditModalContext.CurrentStatus
                    })
                  }
                  size={IconButtonSize.BASE}
                  className="mt-4 w-12"
                  disabled={!permissions.canChangeAvailablebyDate}
                />
              </div>
            ) : (
              <div className="flex w-full justify-end gap-2 items-center">
                <p className="text-xs font-medium text-content-300">
                  PMS value:{" "}
                  {unit.pms_available_at
                    ? moment(unit.available_at).utc().format("MMM D, YYYY")
                    : "--"}
                </p>
                <p
                  className="text-xs text-primary-500 font-bold cursor-pointer"
                  onClick={() =>
                    handleEditUnit({
                      unitId: unit.id,
                      settings: {
                        pms_occupied_override: true,
                        pms_available_at_override: true,
                        occupied: unit.pms_occupied,
                        available_at: unit.pms_available_at
                      }
                    }).then(() => {
                      handleChange("occupied", () => unit.pms_occupied)({});
                      handleChange(
                        "available_at",
                        () => unit.pms_available_at
                      )({});
                    })
                  }
                >
                  Sync with PMS?
                </p>
              </div>
            ))}
        </div>
        <div className="mt-auto">
          <Button
            variant={
              !unit.can_delete || unit.pms_id
                ? ButtonVariant.BASE
                : ButtonVariant.TEXT
            }
            size={ButtonSize.SM}
            className="mb-4 flex items-center gap-2"
            borderLess={!unit.can_delete}
            onClick={() => {
              setShowDeleteUnitModal(true);
            }}
            Icon={Trash02}
            disabled={!unit.can_delete || Boolean(unit.pms_id)}
            tooltipZIndex={41}
            tooltip={
              unit.pms_id
                ? "You can't delete a PMS unit"
                : !unit.can_delete
                  ? "You can't delete a unit with applications"
                  : undefined
            }
          >
            Delete unit
          </Button>
          <div className="-mx-6 mb-4 border-b border-alpha-100" />
          <div className="flex w-full gap-2">
            <div className="w-full">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                className="w-full"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </Button>
            </div>
            <div className="w-full">
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                className="w-full"
                type="submit"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Drawer>
  );
};
